import { Component, OnInit, AfterViewInit, ViewChild, TemplateRef, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableComponent, TableColumn, PaginationConfig } from '../primeng';
import { ModalComponent } from '../primeng/modal/modal.component';

export interface Delegate {
  id: string;
  delegateName: string;
  delegateEmail: string;
  delegateCreatedAt: string;
}

export interface Owner {
  id: string;
  ownerName: string;
  ownerEmail: string;
  ownerCreatedAt: string;
}

@Component({
  selector: 'app-delegate',
  standalone: true,
  imports: [CommonModule, FormsModule, TableComponent, ModalComponent],
  templateUrl: './delegate.component.html',
  styleUrl: './delegate.component.scss',
})
export class DelegateComponent implements OnInit, AfterViewInit {
  @ViewChild('delegateActionCell', { static: false }) delegateActionCellTemplate!: TemplateRef<any>;
  @ViewChild('ownerActionCell', { static: false }) ownerActionCellTemplate!: TemplateRef<any>;

  activeTab: 'delegates' | 'owners' = 'delegates';

  // Delegates columns
  delegateColumns: TableColumn[] = [
    {
      key: 'delegateName',
      label: 'Delegate Name',
      sortable: true,
      width: '25%'
    },
    {
      key: 'delegateEmail',
      label: 'E-Mail',
      sortable: true,
      width: '30%'
    },
    {
      key: 'delegateCreatedAt',
      label: 'Created At',
      sortable: true,
      width: '25%'
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      width: '20%',
      align: 'center',
      fixed: 'right'
    }
  ];

  // Owners columns
  ownerColumns: TableColumn[] = [
    {
      key: 'ownerName',
      label: 'Owner Name',
      sortable: true,
      width: '25%'
    },
    {
      key: 'ownerEmail',
      label: 'E-Mail',
      sortable: true,
      width: '30%'
    },
    {
      key: 'ownerCreatedAt',
      label: 'Created At',
      sortable: true,
      width: '25%'
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      width: '20%',
      align: 'center',
      fixed: 'right'
    }
  ];

  delegatesData: Delegate[] = [];
  ownersData: Owner[] = [];
  
  delegatePagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100]
  };

  ownerPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100]
  };

  loading = false;
  delegateEmptyMessage = 'No delegates found';
  ownerEmptyMessage = 'No owners found';
  hasPendingRequest = false; // Set to true if user has sent a request for organization
  
  // Action dropdown state
  openDelegateDropdownId: string | null = null;
  openOwnerDropdownId: string | null = null;

  // Create Delegate Modal
  showCreateDelegateModal = false;
  newDelegateForm = {
    delegateName: '',
    delegateEmail: ''
  };

  constructor(private cdr: ChangeDetectorRef) {}

  onCreateNewDelegate(): void {
    // Reset form
    this.newDelegateForm = {
      delegateName: '',
      delegateEmail: ''
    };
    // Open modal
    this.showCreateDelegateModal = true;
  }

  onCloseCreateDelegateModal(): void {
    this.showCreateDelegateModal = false;
    // Reset form when closing
    this.newDelegateForm = {
      delegateName: '',
      delegateEmail: ''
    };
  }

  onSubmitCreateDelegate(): void {
    // TODO: Implement API call to create delegate
    console.log('Creating delegate:', this.newDelegateForm);
    
    // Validate form
    if (!this.newDelegateForm.delegateName || !this.newDelegateForm.delegateEmail) {
      console.warn('Please fill in all required fields');
      return;
    }

    // TODO: Call API to create delegate
    // After successful creation, reload delegates list
    // this.loadDelegates();
    
    // Close modal
    this.onCloseCreateDelegateModal();
  }

  get isCreateDelegateFormValid(): boolean {
    return !!(
      this.newDelegateForm.delegateName?.trim() &&
      this.newDelegateForm.delegateEmail?.trim() &&
      this.isValidEmail(this.newDelegateForm.delegateEmail)
    );
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  ngOnInit(): void {
    this.checkPendingRequest();
    if (!this.hasPendingRequest) {
      this.loadDelegates();
      this.loadOwners();
    }
  }

  ngAfterViewInit(): void {
    // Assign templates synchronously to avoid race condition with data loading
    if (this.delegateActionCellTemplate) {
      const actionColumn = this.delegateColumns.find(col => col.key === 'action');
      if (actionColumn) {
        actionColumn.cellTemplate = this.delegateActionCellTemplate;
        this.cdr.detectChanges();
      }
    }
    if (this.ownerActionCellTemplate) {
      const actionColumn = this.ownerColumns.find(col => col.key === 'action');
      if (actionColumn) {
        actionColumn.cellTemplate = this.ownerActionCellTemplate;
        this.cdr.detectChanges();
      }
    }
  }

  setActiveTab(tab: 'delegates' | 'owners'): void {
    this.activeTab = tab;
    // Reassign templates synchronously when switching tabs
    if (tab === 'delegates' && this.delegateActionCellTemplate) {
      const actionColumn = this.delegateColumns.find(col => col.key === 'action');
      if (actionColumn) {
        actionColumn.cellTemplate = this.delegateActionCellTemplate;
        this.cdr.detectChanges();
      }
    }
    if (tab === 'owners' && this.ownerActionCellTemplate) {
      const actionColumn = this.ownerColumns.find(col => col.key === 'action');
      if (actionColumn) {
        actionColumn.cellTemplate = this.ownerActionCellTemplate;
        this.cdr.detectChanges();
      }
    }
  }

  checkPendingRequest(): void {
    // TODO: Replace with actual API call to check if user has pending organization request
    // For now, using mock data - set to true to see the approval message
    this.hasPendingRequest = false; // Change to true to test the approval message
  }

  loadDelegates(): void {
    // Mock data - replace with actual API call
    this.delegatesData = [
      {
        id: '1',
        delegateName: 'John Smith',
        delegateEmail: 'john.smith@example.com',
        delegateCreatedAt: '2024-01-15 10:30 AM'
      },
      {
        id: '2',
        delegateName: 'Sarah Johnson',
        delegateEmail: 'sarah.johnson@example.com',
        delegateCreatedAt: '2024-01-20 02:15 PM'
      },
      {
        id: '3',
        delegateName: 'Michael Brown',
        delegateEmail: 'michael.brown@example.com',
        delegateCreatedAt: '2024-02-01 09:45 AM'
      },
      {
        id: '4',
        delegateName: 'Emily Davis',
        delegateEmail: 'emily.davis@example.com',
        delegateCreatedAt: '2024-02-10 11:20 AM'
      },
      {
        id: '5',
        delegateName: 'David Wilson',
        delegateEmail: 'david.wilson@example.com',
        delegateCreatedAt: '2024-02-15 03:30 PM'
      },
      {
        id: '6',
        delegateName: 'Lisa Anderson',
        delegateEmail: 'lisa.anderson@example.com',
        delegateCreatedAt: '2024-02-20 08:15 AM'
      },
      {
        id: '7',
        delegateName: 'Robert Taylor',
        delegateEmail: 'robert.taylor@example.com',
        delegateCreatedAt: '2024-03-01 01:45 PM'
      },
      {
        id: '8',
        delegateName: 'Jennifer Martinez',
        delegateEmail: 'jennifer.martinez@example.com',
        delegateCreatedAt: '2024-03-05 10:00 AM'
      },
      {
        id: '9',
        delegateName: 'William Garcia',
        delegateEmail: 'william.garcia@example.com',
        delegateCreatedAt: '2024-03-10 04:20 PM'
      },
      {
        id: '10',
        delegateName: 'Amanda Rodriguez',
        delegateEmail: 'amanda.rodriguez@example.com',
        delegateCreatedAt: '2024-03-15 09:30 AM'
      },
      {
        id: '11',
        delegateName: 'James Lee',
        delegateEmail: 'james.lee@example.com',
        delegateCreatedAt: '2024-03-20 02:45 PM'
      },
      {
        id: '12',
        delegateName: 'Michelle White',
        delegateEmail: 'michelle.white@example.com',
        delegateCreatedAt: '2024-03-25 11:15 AM'
      }
    ];

    this.delegatePagination.totalItems = this.delegatesData.length;
  }

  loadOwners(): void {
    // Mock data - replace with actual API call
    this.ownersData = [
      {
        id: '1',
        ownerName: 'Admin User',
        ownerEmail: 'admin@example.com',
        ownerCreatedAt: '2024-01-10 09:00 AM'
      },
      {
        id: '2',
        ownerName: 'Manager User',
        ownerEmail: 'manager@example.com',
        ownerCreatedAt: '2024-01-12 10:30 AM'
      },
      {
        id: '3',
        ownerName: 'Supervisor User',
        ownerEmail: 'supervisor@example.com',
        ownerCreatedAt: '2024-01-18 02:15 PM'
      }
    ];

    this.ownerPagination.totalItems = this.ownersData.length;
  }

  onDelegatePageChange(page: number): void {
    this.delegatePagination.currentPage = page;
    // TODO: Implement API call to fetch data for the new page
  }

  onDelegatePageSizeChange(pageSize: number): void {
    this.delegatePagination.pageSize = pageSize;
    this.delegatePagination.currentPage = 1;
    // TODO: Implement API call to fetch data with new page size
  }

  onDelegateSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    // TODO: Implement sorting logic
    console.log('Delegate sort changed:', event);
  }

  onOwnerPageChange(page: number): void {
    this.ownerPagination.currentPage = page;
    // TODO: Implement API call to fetch data for the new page
  }

  onOwnerPageSizeChange(pageSize: number): void {
    this.ownerPagination.pageSize = pageSize;
    this.ownerPagination.currentPage = 1;
    // TODO: Implement API call to fetch data with new page size
  }

  onOwnerSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    // TODO: Implement sorting logic
    console.log('Owner sort changed:', event);
  }

  // Action dropdown methods for delegates
  toggleDelegateActionDropdown(event: Event, row: Delegate): void {
    event.stopPropagation();
    const rowId = row.id;
    const wasOpen = this.openDelegateDropdownId === rowId;
    this.openDelegateDropdownId = wasOpen ? null : rowId;
    
    if (!wasOpen && this.openDelegateDropdownId === rowId) {
      setTimeout(() => {
        const button = (event.target as HTMLElement).closest('.action-btn') as HTMLElement;
        const dropdown = document.querySelector(`[data-delegate-dropdown-id="${rowId}"]`) as HTMLElement;
        if (button && dropdown) {
          const rect = button.getBoundingClientRect();
          dropdown.style.position = 'fixed';
          dropdown.style.top = `${rect.bottom + 4}px`;
          dropdown.style.left = `${rect.left}px`;
          dropdown.style.width = `${rect.width}px`;
          dropdown.style.minWidth = `${rect.width}px`;
        }
      }, 0);
    }
  }

  closeDelegateActionDropdown(): void {
    this.openDelegateDropdownId = null;
  }

  isDelegateDropdownOpen(row: Delegate): boolean {
    return this.openDelegateDropdownId === row.id;
  }

  // Action dropdown methods for owners
  toggleOwnerActionDropdown(event: Event, row: Owner): void {
    event.stopPropagation();
    const rowId = row.id;
    const wasOpen = this.openOwnerDropdownId === rowId;
    this.openOwnerDropdownId = wasOpen ? null : rowId;
    
    if (!wasOpen && this.openOwnerDropdownId === rowId) {
      setTimeout(() => {
        const button = (event.target as HTMLElement).closest('.action-btn') as HTMLElement;
        const dropdown = document.querySelector(`[data-owner-dropdown-id="${rowId}"]`) as HTMLElement;
        if (button && dropdown) {
          const rect = button.getBoundingClientRect();
          dropdown.style.position = 'fixed';
          dropdown.style.top = `${rect.bottom + 4}px`;
          dropdown.style.left = `${rect.left}px`;
          dropdown.style.width = `${rect.width}px`;
          dropdown.style.minWidth = `${rect.width}px`;
        }
      }, 0);
    }
  }

  closeOwnerActionDropdown(): void {
    this.openOwnerDropdownId = null;
  }

  isOwnerDropdownOpen(row: Owner): boolean {
    return this.openOwnerDropdownId === row.id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDelegateDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeDelegateActionDropdown();
    }
    if (this.openOwnerDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeOwnerActionDropdown();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.openDelegateDropdownId !== null) {
      this.closeDelegateActionDropdown();
    }
    if (this.openOwnerDropdownId !== null) {
      this.closeOwnerActionDropdown();
    }
  }

  handleDelegateAction(action: string, row: Delegate): void {
    this.closeDelegateActionDropdown();
    
    switch (action) {
      case 'delete':
        console.log('Delete delegate:', row);
        // TODO: Implement delete delegate logic (show confirmation dialog)
        break;
      default:
        console.log('Unknown action:', action, row);
    }
  }

  handleOwnerAction(action: string, row: Owner): void {
    this.closeOwnerActionDropdown();
    
    switch (action) {
      case 'withdraw':
        console.log('Withdraw owner:', row);
        // TODO: Implement withdraw owner logic (show confirmation dialog)
        break;
      default:
        console.log('Unknown action:', action, row);
    }
  }
}

